import * as Form from "@radix-ui/react-form"
import * as Select from "@radix-ui/react-select"
import * as VisuallyHidden from "@radix-ui/react-visually-hidden"
import { ReactComponent as ChevronSVG } from "images/global/chevron.svg"
import { ReactComponent as CheckSVG } from "images/global/form-checkbox.svg"
import { eases } from "library/eases"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import { generateGradientBorder } from "library/generateGradientBorder"
import { useCallback, useRef, useState } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import media from "styles/media"
import textStyles from "styles/text"

export default function FormDropdown({
	placeholder,
	options,
	name,
	required,
	className,
}: {
	name: string
	placeholder: string
	options: string[]
	required?: boolean
	className?: string
}) {
	const [contentHeight, setContentHeight] = useState(0)
	const [open, setOpen] = useState(false)
	const [value, setValue] = useState<string>()

	const containerRef = useRef<HTMLDivElement>(null)
	const menuRef = useRef<HTMLDivElement | null>(null)

	const showCheck = value && !open

	const scrollToTheEndOfTheMenu = useCallback(() => {
		setTimeout(() => {
			if (menuRef.current && containerRef.current) {
				const menuRect = menuRef.current.getBoundingClientRect()
				const bottomOverflow = menuRect.bottom - window.innerHeight

				if (bottomOverflow > 0) {
					window.scrollBy({
						top: bottomOverflow + 16,
						behavior: "smooth",
					})
				}
			}
		}, 0)
	}, [])

	return (
		<Form.Field
			name={name}
			style={{ position: "relative" }}
			className={className}
			ref={containerRef}
		>
			<VisuallyHidden.Root>
				<Form.Control tabIndex={-1} required={required} value={value ?? ""} />
			</VisuallyHidden.Root>
			<Select.Root
				onValueChange={setValue}
				onOpenChange={(open) => {
					setOpen(open)
					if (open) scrollToTheEndOfTheMenu()
				}}
				open={open}
				name={name}
			>
				<Wrapper
					style={{
						"--content-height": `${contentHeight}px`,
						"--not-open": open ? 0 : 1,
					}}
				>
					<Trigger>
						<Select.Value
							placeholder={placeholder}
							data-role={value ? "value" : "placeholder"}
						/>
						<Icon>
							<Check
								style={{
									opacity: showCheck ? 1 : 0,
									// the desired effect here is that this will appear to flip while crossfading
									scale: showCheck ? "1 1" : "1 -1",
								}}
							/>
							<Chevron
								style={{
									scale: open ? "1 1" : "1 -1",
									opacity: showCheck ? 0 : 1,
								}}
							/>
						</Icon>
					</Trigger>
					<Content
						ref={(item) => {
							menuRef.current = item
							setContentHeight(item?.clientHeight ?? 0)
						}}
						position="popper"
						avoidCollisions={false}
					>
						{options.map((option) => (
							<Item value={option} key={option}>
								<ItemText>{option}</ItemText>
							</Item>
						))}
					</Content>
					{/* content instantly disappears when closed, so we need to provide a visual fallback */}
					{!open && (
						<Content as="div" aria-hidden data-role="fallback">
							{options.map((option) => (
								<Item value={option} as="div" key={option}>
									<ItemText as="div">{option}</ItemText>
								</Item>
							))}
						</Content>
					)}
				</Wrapper>
			</Select.Root>
			{!value && (
				<FormMessage
					style={{ opacity: open ? 0 : 1 }}
					match="valueMissing"
					data-role="error"
				>
					This field is required
				</FormMessage>
			)}
		</Form.Field>
	)
}

const Icon = styled(Select.Icon)`
	display: grid;
	place-items: center;

	> * {
		grid-area: 1/1/2/2;
	}
`

const Chevron = styled(ChevronSVG)`
	transition:
		scale 0.2s ${eases.quad.out},
		opacity 0.2s ${eases.quad.out};

	${fresponsive(css`
		width: 12px;
		height: 12px;
	`)}
`

const Check = styled(CheckSVG)`
	transition:
		scale 0.2s ${eases.quad.out},
		opacity 0.2s ${eases.quad.out};

	${fresponsive(css`
		width: 8px;
		height: 8px;
	`)}
`

const Wrapper = styled.div`
	margin-bottom: calc(var(--content-height) * -1);
	background: linear-gradient(
		228deg,
		rgb(36 36 36 / 25%) -27%,
		rgb(138 138 138 / 25%) 192.85%
	);
	overflow: clip;
	will-change: transform;
	transition: 0.5s ${eases.quad.out};
	transition-property: height, margin-bottom, color;
	position: relative;
	color: ${colors.gray500};
	pointer-events: all;
	z-index: 1;
	${fresponsive(css`
		--height: 48px;

		height: calc(var(--content-height) + var(--height));
		width: 100%;
		border-radius: 25px;
		backdrop-filter: blur(4px);
	`)}
	${ftablet(css`
		--height: 60px;

		border-radius: 33px;
	`)}
	${fmobile(css`
		--height: 60px;

		border-radius: 33px;
	`)}

	&::before,
	&::after {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		pointer-events: none;
	}

	/* static border */
	&::before {
		${generateGradientBorder(
			"linear-gradient(17deg, #252525 16.5%, #8B8B8B 218.45%)",
			1,
		)}
	}

	/* hover border */
	&::after {
		opacity: 0;
		transition: opacity 0.2s ${eases.quad.out};
		${generateGradientBorder(
			"linear-gradient(173deg, #FFF -49.86%, rgba(255, 255, 255, 0.00) 142.67%)",
			1,
		)}
	}

	${media.hover} {
		&:hover {
			color: ${colors.gray400};

			&::after {
				opacity: var(--not-open);
			}
		}
	}

	&:focus-within {
		color: ${colors.gray400};

		&::after {
			opacity: var(--not-open);
		}
	}
`

const Trigger = styled(Select.Trigger)`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	cursor: pointer;
	height: var(--height);
	${fresponsive(css`
		padding: 0 24px;
	`)}

	span {
		${textStyles.kicker3};
		${ftablet(css`
			${textStyles.kicker2};
		`)}
		${fmobile(css`
			${textStyles.kicker2};
		`)}
	}

	&:focus {
		outline: none;
	}

	&:not([data-placeholder]) {
		color: ${colors.blueWhite};
	}
`

const Content = styled(Select.Content)`
	${fresponsive(css`
		padding-top: 2px;
		padding-bottom: 14px;
		border-top: 1px solid ${colors.blackLight};
		width: calc(var(--radix-select-trigger-width) - 48px);
		margin: 0 24px;
	`)}
	${ftablet(css`
		padding-bottom: 20px;
	`)}
`

const Item = styled(Select.Item)`
	${textStyles.kicker3};
	color: ${colors.gray400};
	transition: color 0.1s ${eases.quad.out};
	cursor: pointer;
	${fresponsive(css`
		padding: 14px 8px;
	`)}
	${ftablet(css`
		${textStyles.kicker2};
	`)}
	${fmobile(css`
		${textStyles.kicker2};
	`)}


	&:hover {
		color: ${colors.blueWhite};
	}

	&:focus {
		outline: none;
		color: ${colors.blueWhite};
	}

	* {
		font-family: inherit;
	}
`

const ItemText = styled(Select.ItemText)``

const FormMessage = styled(Form.Message)`
	transition: opacity 0.2s ${eases.quad.out};
	position: absolute;
	${textStyles.body2Mini};
	color: #ff5151;
	width: 100%;
	${fresponsive(css`
		top: calc(100% + 2.5px);
		left: 24px;
	`)}
`
